import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHead } from "../components/ui-bits";
import { toast } from "sonner";
import { CheckCircle, Clock, WarningCircle, EnvelopeSimple, ShieldCheck } from "@phosphor-icons/react";

const cfg = {
  overdue: { color: "#FF3B30", Icon: WarningCircle, label: "Overdue" },
  upcoming: { color: "#B8860B", Icon: Clock, label: "Upcoming" },
  completed: { color: "#00A86B", Icon: CheckCircle, label: "Completed" },
};

export default function Compliance() {
  const [items, setItems] = useState([]);
  const [scores, setScores] = useState(null);
  useEffect(() => {
    api.get("/compliance").then((r) => setItems(r.data));
    api.get("/scores").then((r) => setScores(r.data.compliance));
  }, []);

  const remind = async () => {
    const { data } = await api.post("/compliance/remind", {});
    toast.success(data.sent ? `Reminder emailed — ${data.count} upcoming deadline(s)` : "No upcoming deadlines 🎉");
  };

  const counts = {
    overdue: items.filter((i) => i.status === "overdue").length,
    upcoming: items.filter((i) => i.status === "upcoming").length,
    completed: items.filter((i) => i.status === "completed").length,
  };
  const overall = scores?.Overall;

  return (
    <div>
      <PageHead eyebrow="Statutory Deadlines" title="Compliance Calendar">
        <button data-testid="remind-me" onClick={remind}
          className="border border-black/20 hover:bg-black/5 px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors">
          <EnvelopeSimple size={16} /> Email me my deadlines
        </button>
      </PageHead>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="card p-6 bg-[#111111] text-white flex items-center gap-5">
          <div className="relative w-20 h-20 shrink-0">
            <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#00A86B" strokeWidth="3"
                strokeDasharray={`${overall || 0} 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center"><ShieldCheck size={22} color="#00A86B" weight="fill" /></div>
          </div>
          <div>
            <p className="label text-white/50 mb-1">Compliance Score</p>
            <p className="font-head font-black text-4xl tnum">{overall ?? "—"}<span className="text-white/40 text-lg">%</span></p>
          </div>
        </div>
        <div className="card p-6 lg:col-span-2">
          <p className="label mb-4">Score by Area</p>
          <div className="space-y-3">
            {scores && Object.entries(scores).filter(([k]) => k !== "Overall").map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-xs mb-1"><span className="text-[#555]">{k}</span><span className="tnum font-semibold">{v}%</span></div>
                <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
                  <div style={{ width: `${v}%`, background: v >= 95 ? "#00A86B" : v >= 90 ? "#002FA7" : "#FF3B30" }} className="h-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {["overdue", "upcoming", "completed"].map((g) => {
          const { color, Icon, label } = cfg[g];
          const list = items.filter((i) => i.status === g).sort((a, b) => a.date.localeCompare(b.date));
          return (
            <div key={g} className="card overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-black/10" style={{ background: `${color}0d` }}>
                <span className="flex items-center gap-2 font-head font-bold text-sm" style={{ color }}>
                  <Icon size={18} weight="fill" /> {label}
                </span>
                <span className="tnum text-sm font-bold" style={{ color }}>{counts[g]}</span>
              </div>
              <div className="p-3 space-y-2 min-h-[120px]">
                {list.map((i, idx) => (
                  <div key={i.id || idx} data-testid={`compliance-${g}-${idx}`} className="border-l-4 rounded-sm bg-black/[0.02] px-3 py-2.5" style={{ borderLeftColor: color }}>
                    <p className="text-sm font-medium">{i.name}</p>
                    <p className="text-xs text-[#888] tnum mt-0.5">Due {i.date}</p>
                  </div>
                ))}
                {list.length === 0 && <p className="text-xs text-[#bbb] text-center py-6">Nothing here.</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
